import React from 'react'
import useSWR, { SWRConfig } from 'swr'
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'

import LoginPage from './pages/login'
import LandingPage from './pages/landing'
import DashboardPage from './pages/dashboard'
import { getCurrentUser, logout, updateUserTimezone } from './api'
import { AuthContext, useAuth } from './context/auth'
import MasterPage from './pages/master'
import NewInvoicePage from './pages/invoices/new'
import ClientsPage from './pages/clients'
import UserProfile from './pages/user/profile'
import EditInvoicePage from './pages/invoices/edit'
import NewClientPage from './pages/clients/new'
import EditClientPage from './pages/clients/edit'
import { useAlerts } from './context/alerts'
import Spinner from './components/ui/spinner'
import Button from './components/ui/button'
import SendEmailPage from './pages/email'
import Terms from './pages/legal/terms'
import Privacy from './pages/legal/privacy'
import Faq from './pages/legal/faq'
import PricingPage from './pages/pricing'
import BlogList from './pages/blog/BlogList'
import BlogPostPage from './pages/blog/BlogPostPage'
import AboutPage from './pages/about'
import AdminPanel from './pages/admin'
import AdminPlayground from './pages/admin/playground'
import AdminFreeAccess from './pages/admin/free-access'
import AdminUserInvoices from './pages/admin/user-invoices'
import config from './config'
import GetPremiumPage from './pages/get-premium'
import BillingPage from './pages/billing'
import NewInvoiceLink from './components/billing/NewInvoiceLink'
import { InvoicePagesShell } from './pages/master/invoice'
import ScrollToTop from './components/ScrollToTop'
import { appendAlertSearchParams } from './hooks/useAlertFromSearchParams'
import { AUTH_REDIRECT_PATH_KEY } from './lib/signIn'

const EditInvoiceRoute = () => {
  const { id } = useParams()
  return (
    <InvoicePagesShell activeInvoiceId={id}>
      <EditInvoicePage />
    </InvoicePagesShell>
  )
}

function App() {
  const { addAlert } = useAlerts()
  const navigate = useNavigate()
  const swrConfig = {
    onError: (error: any) => {
      if (error?.redirectTo) {
        const path = error.message
          ? appendAlertSearchParams(error.redirectTo, error.message, 'error')
          : error.redirectTo
        navigate(path)
        return
      }
      addAlert({ type: 'error', message: error?.message })
    }
  }

  return (
    <SWRConfig value={swrConfig}>
      <AuthProvider>
        <ScrollToTop />
        <Routes>
          <Route path='/terms' element={<Terms />} />
          <Route path='/privacy' element={<Privacy />} />
          <Route path='/faq' element={<Faq />} />
          <Route path='/about' element={<AboutPage />} />
          <Route path='/pricing' element={<PricingPage />} />
          <Route path='/blog' element={<BlogList />} />
          <Route path='/blog/:slug' element={<BlogPostPage />} />
          <Route path='/' element={<LandingPage />} />
          <Route path='/login' element={<LoginPage />} />
          <Route path='/signup' element={<LoginPage />} />
          <Route path='/get-premium' element={<RequireAuth><GetPremiumPage /></RequireAuth>} />
          <Route path='/billing' element={<RequireAuth><MasterPage header='Billing'><BillingPage /></MasterPage></RequireAuth>} />
          <Route
            path='/onboard-step-1'
            element={(
              <RequireAuth>
                <Navigate to='/invoices/new' replace />
              </RequireAuth>
            )}
          />
          <Route
            path='/onboard-step-2'
            element={(
              <RequireAuth>
                <Navigate to='/invoices/new' replace />
              </RequireAuth>
            )}
          />
          <Route
            path='/onboard-step-3'
            element={(
              <RequireAuth>
                <Navigate to='/invoices/new' replace />
              </RequireAuth>
            )}
          />
          <Route
            path='/onboard-step-4'
            element={(
              <RequireAuth>
                <Navigate to='/invoices/new' replace />
              </RequireAuth>
            )}
          />
          <Route
            path='/dashboard'
            element={(
              <RequireAuth>
                <MasterPage header='Dashboard' actions={<NewInvoiceLink />} hideBackButton stickyHeader>
                  <DashboardPage />
                </MasterPage>
              </RequireAuth>
            )}
          />
          <Route
            path='/invoices/new'
            element={(
              <RequireAuth>
                <InvoicePagesShell>
                  <NewInvoicePage />
                </InvoicePagesShell>
              </RequireAuth>
            )}
          />
          <Route
            path='/invoices/:id/send-email'
            element={(
              <RequireAuth>
                <MasterPage header='Send Email'>
                  <div className='container-with-bg'>
                    <SendEmailPage />
                  </div>
                </MasterPage>
              </RequireAuth>
            )}
          />
          <Route
            path='/invoices/:id'
            element={(
              <RequireAuth>
                <EditInvoiceRoute />
              </RequireAuth>
            )}
          />
          <Route
            path='/clients'
            element={(
              <RequireAuth>
                <MasterPage header='Your Clients' description='Add or edit clients.' backTo='/dashboard' actions={<NavLink to='/clients/new'><Button>Create Client</Button></NavLink>} stickyHeader>
                  <ClientsPage />
                </MasterPage>
              </RequireAuth>
            )}
          />
          <Route
            path='/clients/new'
            element={(
              <RequireAuth>
                <MasterPage header='New Client'>
                  <div className='container-with-bg'>
                    <NewClientPage />
                  </div>
                </MasterPage>
              </RequireAuth>
            )}
          />
          <Route
            path='/clients/:id'
            element={(
              <RequireAuth>
                <MasterPage header='Edit Client' stickyHeader>
                  <div className='container-with-bg'>
                    <EditClientPage />
                  </div>
                </MasterPage>
              </RequireAuth>
            )}
          />
          <Route
            path='/profile/logo'
            element={(
              <RequireAuth>
                <Navigate to='/profile' replace />
              </RequireAuth>
            )}
          />
          <Route
            path='/profile'
            element={(
              <RequireAuth>
                <MasterPage header='Your Profile' description='Edit your details and setup automatic follow-up emails.' backTo='/dashboard' stickyHeader>
                  <UserProfile />
                </MasterPage>
              </RequireAuth>
            )}
          />

          <Route
            path='/admin'
            element={(
              <RequireAuth>
                <RequireAdmin>
                  <MasterPage header='Admin'>
                    <AdminPanel />
                  </MasterPage>
                </RequireAdmin>
              </RequireAuth>
            )}
          />

          <Route
            path='/admin/playground'
            element={(
              <RequireAuth>
                <RequireAdmin>
                  <MasterPage header='Admin Playground'>
                    <AdminPlayground />
                  </MasterPage>
                </RequireAdmin>
              </RequireAuth>
            )}
          />
          <Route
            path='/admin/free-access'
            element={(
              <RequireAuth>
                <RequireAdmin>
                  <MasterPage header='Free access whitelist'>
                    <AdminFreeAccess />
                  </MasterPage>
                </RequireAdmin>
              </RequireAuth>
            )}
          />
          <Route
            path='/admin/users/:userId/invoices'
            element={(
              <RequireAuth>
                <RequireAdmin>
                  <MasterPage header='User invoices'>
                    <AdminUserInvoices />
                  </MasterPage>
                </RequireAdmin>
              </RequireAuth>
            )}
          />

        </Routes>
      </AuthProvider>
    </SWRConfig>
  )
}

function RequireAuth({ children }: { children: JSX.Element }) {
  let { user } = useAuth()
  let location = useLocation()
  if (!user) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    const currentPath = window.location.pathname;
    localStorage.setItem(AUTH_REDIRECT_PATH_KEY, currentPath)
    return <Navigate to='/login' state={{ from: location }} replace />
  }

  return children
}


function RequireAdmin({ children }: { children: JSX.Element }) {
  let { user } = useAuth()
  if (!user.isAdmin) {
    return <Navigate to='/' replace />
  }
  return children
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { mutate, data, isLoading } = useSWR('/api/auth/user', getCurrentUser)
  const user = data?.data

  React.useEffect(() => {
    if (!user?._id) return
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!timezone || timezone === user.timezone) return
    updateUserTimezone(timezone).then((res) => {
      if (res?.data) mutate()
    })
  }, [user?._id, user?.timezone, mutate])

  const redirectToOAuth = (provider: 'google' | 'microsoft') => {
    const url = `${config.REACT_APP_API_URL}/api/auth/${provider}?from=${encodeURIComponent(window.location.pathname)}`
    window.open(url, '_self')
  }

  let signin = () => redirectToOAuth('google')
  let signInMicrosoft = () => redirectToOAuth('microsoft')

  let signout = async() => {
    await logout()
    mutate()
  };

  let value = { user, signin, signInMicrosoft, signout, mutate };
  if(isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center min-vh-100'>
        <Spinner />
      </div>
    )
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default App
